import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import { toast } from "react-toastify";
import PaginationComponent from "../../Component/Pagination/pagination";
import moment from "moment";
import Loader from "../../Component/Loader/Loader";

// --- NEW: Import Recharts ---
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from 'recharts';

interface StatItem {
    value: number;
    growth: number;
}

interface CommissionStats {
    totalCommission: StatItem;
    pendingPayout: StatItem;
    totalPaidOut: StatItem;
    totalTransactions: StatItem;
    avgRate: StatItem;
}

const PIE_COLORS = ['#00C49F', '#0088FE', '#FFBB28', '#FF8042', '#a28CFE', '#FF6B6B'];

const AffiliateComission = () => {
    const navigate = useNavigate();

    const [commissionList, setCommissionList] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [searchText, setSearchText] = useState("");

    const [dateFilter, setDateFilter] = useState<string>("last7days");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    const [stats, setStats] = useState<CommissionStats>({
        totalCommission: { value: 0, growth: 0 },
        pendingPayout: { value: 0, growth: 0 },
        totalPaidOut: { value: 0, growth: 0 },
        totalTransactions: { value: 0, growth: 0 },
        avgRate: { value: 0, growth: 0 }
    });

    // --- NEW: Chart States ---
    const [dailyData, setDailyData] = useState<any[]>([]);
    const [tierData, setTierData] = useState<any[]>([]);

    useEffect(() => {
        let start = "";
        let end = "";

        if (dateFilter === "last7days") {
            start = moment().subtract(7, 'days').format('YYYY-MM-DD');
            end = moment().format('YYYY-MM-DD');
        } else if (dateFilter === "thisMonth") {
            start = moment().startOf('month').format('YYYY-MM-DD');
            end = moment().endOf('month').format('YYYY-MM-DD');
        } else if (dateFilter === "lastMonth") {
            start = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
            end = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        } else if (dateFilter === "allTime") {
            start = "";
            end = "";
        }

        setStartDate(start);
        setEndDate(end);
        setPage(1); 
    }, [dateFilter]);

    const fetchCommissionList = async () => {
        if (dateFilter !== "allTime" && (!startDate || !endDate)) return; 

        setLoading(true);
        try {
            const response = await getRequest(
                `/affiliate/affiliate-commission-list?page=${page}&limit=10&search=${search}`
            );

            if (response.status === "success") {
                setCommissionList(response.data);
                setTotalPages(response.pagination.totalPages);
                setTotalUsers(response.pagination.totalAffiliates);
                
                if (response.stats) setStats(response.stats);
                
                // Set Chart Data
                if (response.charts) {
                    setDailyData(response.charts.daily || []);
                    setTierData(response.charts.tiers || []);
                }

            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error("Error fetching Commissions:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCommissionList();
    }, [page, search, startDate, endDate]);

    useEffect(() => {
        const delay = setTimeout(() => {
            if (searchText.length >= 3 || searchText.length === 0) {
                setPage(1);
                setSearch(searchText);
            }
        }, 500);

        return () => clearTimeout(delay);
    }, [searchText]);

    const GrowthBadge = ({ growth }: { growth: number }) => {
        const isPositive = growth >= 0;
        return (
            <div className="d-flex align-items-center gap-2 mt-1">
                <span className={`badge badge-sr ${isPositive ? 'bg-green-light-5 text-success' : 'bg-red-light-5 text-danger'} mb-0`}>
                    <i className={`ri-arrow-${isPositive ? 'up' : 'down'}-line align-middle`}></i> {Math.abs(growth).toFixed(2)} % 
                </span>
                <span className="fs-13 text-muted">vs. previous period</span>
            </div>
        );
    };

    return (
        <div className="hk-pg-wrapper">
            {loading && (<Loader />)}
            <div className="hk-pg-body py-0">
                <div className="page-wrap">
                    <div className="page-content ps-0">
                        <div className="page-detail-wrap">
                            <header className="page-header">
                                <div>
                                    <div className="page-title">
                                        <h2 className="mb-0">Commission Ledger</h2>
                                    </div>
                                    <p className="text-muted mb-0">All commission transactions across affiliates</p>
                                </div>
                                <div className="page-options-wrap">
                                    <button type="button" className="btn btn-dark btn-rounded" onClick={() => navigate('/affiliate-program/commission/commission-tier')}>
                                        <span>
                                            <span className="icon"><i className="ri-add-line bticon"></i></span>
                                            <span>Commission Tier</span>
                                        </span>
                                    </button> 
                                    <a className="btn btn-icon btn-flush-dark btn-rounded flush-soft-hover hk-navbar-togglable d-lg-inline-block d-none"
                                        href="#" data-bs-toggle="tooltip" data-bs-placement="top" title=""
                                        data-bs-original-title="Collapse">
                                        <span className="btn-icon-wrap">
                                            <span className="feather-icon"><i className="ri-arrow-up-s-line ri-lg"></i></span>
                                            <span className="feather-icon d-none"><i className="ri-arrow-down-s-line ri-lg"></i></span>
                                        </span>
                                    </a>
                                </div>
                            </header>
                            <div className="page-body">
                                <div data-simplebar className="nicescroll-bar">
                                    <div className="container-fluid">
                                        <div className="row">
                                            <div className="col">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between">
                                                            <div>
                                                                <h6 className="h2 mb-1">₹{stats.totalCommission.value.toFixed(2)}</h6>
                                                                <p className="mb-1 text-muted fs-12">Total earned</p>
                                                            </div>
                                                            <div className="flex-shrink-0">
                                                                <div className="avatar avatar-sm avatar-icon avatar-soft-success avatar-smmm">
                                                                    <span className="initial-wrap"><i className="ri-store-3-fill"></i></span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <GrowthBadge growth={stats.totalCommission.growth} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between">
                                                            <div>
                                                                <h6 className="h2 mb-1">₹{stats.pendingPayout.value.toFixed(2)}</h6>
                                                                <p className="mb-1 text-muted fs-12">Pending payout</p>
                                                            </div>
                                                            <div className="flex-shrink-0">
                                                                <div className="avatar avatar-sm avatar-icon avatar-soft-success avatar-smmm">
                                                                    <span className="initial-wrap"><i className="ri-store-3-fill"></i></span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <GrowthBadge growth={stats.pendingPayout.growth} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between">
                                                            <div>
                                                                <h6 className="h2 mb-1">₹{stats.totalPaidOut.value.toFixed(2)}</h6>
                                                                <p className="mb-1 text-muted fs-12">Total paid out</p>
                                                            </div>
                                                            <div className="flex-shrink-0">
                                                                <div className="avatar avatar-sm avatar-icon avatar-soft-success avatar-smmm">
                                                                    <span className="initial-wrap"><i className="ri-store-3-fill"></i></span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <GrowthBadge growth={stats.totalPaidOut.growth} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between">
                                                            <div>
                                                                <h6 className="h2 mb-1">{stats.totalTransactions.value}</h6>
                                                                <p className="mb-1 text-muted fs-12">Transactions</p>
                                                            </div>
                                                            <div className="flex-shrink-0">
                                                                <div className="avatar avatar-sm avatar-icon avatar-soft-success avatar-smmm">
                                                                    <span className="initial-wrap"><i className="ri-store-3-fill"></i></span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <GrowthBadge growth={stats.totalTransactions.growth} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between">
                                                            <div>
                                                                <h6 className="h2 mb-1">₹{stats.avgRate.value.toFixed(2)}</h6>
                                                                <p className="mb-1 text-muted fs-12">Avg rate</p>
                                                            </div>
                                                            <div className="flex-shrink-0">
                                                                <div className="avatar avatar-sm avatar-icon avatar-soft-success avatar-smmm">
                                                                    <span className="initial-wrap"><i className="ri-store-3-fill"></i></span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <GrowthBadge growth={stats.avgRate.growth} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* --- CHARTS SECTION --- */}
                                        <div className="row g-3 mb-3">
                                            <div className="col-lg-6">
                                                <div className="card h-100">
                                                    <div className="card-header">
                                                        <h6>Commission (Day Wise)</h6>
                                                    </div>
                                                    <div className="card-body">
                                                        <ResponsiveContainer width="100%" height={250}>
                                                            <AreaChart data={dailyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                                                <defs>
                                                                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                                                                        <stop offset="5%" stopColor="#00C49F" stopOpacity={0.3}/>
                                                                        <stop offset="95%" stopColor="#00C49F" stopOpacity={0}/>
                                                                    </linearGradient>
                                                                </defs>
                                                                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                                                                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(tick) => moment(tick).format('MMM DD')} />
                                                                <YAxis tick={{ fontSize: 11 }} tickFormatter={(val) => `₹${val}`} />
                                                                <Tooltip 
                                                                    formatter={(value: any) => [`₹${Number(value).toFixed(2)}`, 'Commission']} 
                                                                    labelFormatter={(label) => moment(label).format('MMM DD, YYYY')} 
                                                                />
                                                                <Area type="monotone" dataKey="total" stroke="#00C49F" fillOpacity={1} fill="url(#colorTotal)" />
                                                            </AreaChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="card h-100">
                                                    <div className="card-header">
                                                        <h6>By Commission Type</h6>
                                                    </div>
                                                    <div className="card-body">
                                                        {tierData.length > 0 ? (
                                                            <ResponsiveContainer width="100%" height={250}>
                                                                <PieChart>
                                                                    <Pie
                                                                        data={tierData}
                                                                        cx="50%"
                                                                        cy="50%"
                                                                        innerRadius={60}
                                                                        outerRadius={80}
                                                                        fill="#8884d8"
                                                                        paddingAngle={5}
                                                                        dataKey="value"
                                                                    >
                                                                        {tierData.map((entry, index) => (
                                                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                                                        ))}
                                                                    </Pie>
                                                                    <Tooltip formatter={(value: any) => `₹${Number(value).toFixed(2)}`} />
                                                                    <Legend verticalAlign="bottom" height={36} />
                                                                </PieChart>
                                                            </ResponsiveContainer>
                                                        ) : (
                                                            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                                                No commission types found for this period.
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-12">
                                                <div className="card">
                                                    <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                        <div>
                                                            <h6 className="mb-0">Commission ledger lists</h6>
                                                            <p className="mb-0 text-muted fs-12">All commission transactions across affiliates</p>
                                                        </div>
                                                        <div>
                                                            <div className="filters d-flex gap-2 align-items-center">
                                                                <div className="search-wrap">
                                                                    <svg className="s-icon" width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#b0a898" strokeWidth="1.5"><circle cx="6.5" cy="6.5" r="4"></circle><line x1="10" y1="10" x2="14" y2="14"></line></svg>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Search affiliate, order ID…"
                                                                        value={searchText}
                                                                        onChange={(e) => setSearchText(e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="table-responsive">
                                                        <table className="table table-hover mb-0">
                                                            <thead>
                                                                <tr>
                                                                    <th>Order ID</th>
                                                                    <th>Affiliate</th>
                                                                    <th className="text-center">Sale Amount </th>
                                                                    <th className="text-center">Commission / Earning</th>
                                                                    <th className="text-center">Commission Rate</th>
                                                                    <th>Date</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {commissionList && commissionList.length > 0 ? (
                                                                    commissionList.map((item, index) => (
                                                                        <tr key={index}>
                                                                            <td>#{item.ac_unique_id}</td>
                                                                            <td>
                                                                                <div className="media align-items-center">
                                                                                    <div className="media-head me-2">
                                                                                        <div className="avatar avatar-xss rounded">
                                                                                            <img src="/dist/img/logo-avatar-3.png" alt="user" className="avatar-img" />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="media-body">
                                                                                        <h6 className="fs-13 mb-0 eclps-name-175">{item.ac_user_id?.user_first_name + ' ' + item.ac_user_id?.user_last_name}</h6>
                                                                                        <p className="mb-0 fs-12">{item.ac_user_id?.user_email}</p>
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                            <td className="text-center">{item.ac_sale_amount ?? 0}</td>
                                                                            <td className="text-center">{item.ac_amount ?? 0}</td>
                                                                            <td className="text-center">{item.ac_commission_tier_id?.commission_amount ?? 0}%</td>
                                                                            <td>{moment(item.createdAt).format('DD MMM, YYYY')}</td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan={6}>
                                                                            <div className="text-center">No Record Found</div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    <div className="card-footer d-flex justify-content-between align-items-center">
                                                        <div className="col-sm">
                                                            <div className="text-muted">Showing <span className="fw-semibold">{commissionList?.length || 0}</span> of <span className="fw-semibold">{totalUsers || 0}</span> Results</div>
                                                        </div>
                                                        {totalPages > 0 && <PaginationComponent totalPages={totalPages} currentPage={page} setCurrentPage={setPage} />}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AffiliateComission;